// The Wall (home). See sections/pr-wall.liquid and assets/pr-wall.css.
// - Desktop with motion allowed: pins the section (CSS position: sticky) and maps the page's vertical
//   scroll to the wall sliding sideways. Transform only, one rAF per scroll frame, no library.
// - A sprayed progress line shows how much of the wall has gone by, with a tick per catalog number.
// - Posters get .is-pasted as they come into view (a short paste-in; they are visible before and after).
// - Keyboard: tabbing to a poster that is off to the side scrolls the page so the wall brings it in.
// Phones, tablets, reduced motion and short windows keep the plain CSS layout: nothing is pinned.
(() => {
  const root = document.querySelector('[data-pr-wall]');
  if (!root) return;

  const viewport = root.querySelector('[data-pr-wall-viewport]');
  const track = root.querySelector('[data-pr-wall-track]');
  const fill = root.querySelector('[data-pr-wall-fill]');
  const tickList = root.querySelector('[data-pr-wall-ticks]');
  const items = [...root.querySelectorAll('[data-pr-wall-item]')];
  if (!viewport || !track || !items.length) return;

  const wide = window.matchMedia('(min-width: 990px)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MIN_TRAVEL = 120; // the wall must overflow the window by this much to be worth pinning
  const MIN_HEIGHT = 600; // shorter windows cannot fit a poster in the pin

  let pinned = false;
  let travel = 0;
  let width = 0;
  let length = 0;
  let centers = [];
  let ticks = [];
  let ticking = false;

  const clamp = (n) => Math.min(1, Math.max(0, n));

  // ---- Ticks: one per poster, placed where the poster sits along the wall ----
  const buildTicks = () => {
    if (!tickList) return;
    ticks = items.map((item, i) => {
      const li = document.createElement('li');
      li.textContent = item.dataset.catalog || String(i + 1).padStart(2, '0');
      li.style.setProperty('--at', (centers[i] / length).toFixed(4));
      return li;
    });
    tickList.replaceChildren(...ticks);
  };

  const unpin = () => {
    pinned = false;
    root.classList.remove('is-pinned');
    root.style.removeProperty('--pr-wall-travel');
    track.style.transform = '';
  };

  // The strip layout is the same pinned or not, so it can be measured in either state
  const measure = () => {
    if (!wide.matches || reduce.matches || window.innerHeight < MIN_HEIGHT) return unpin();

    width = viewport.clientWidth;
    length = track.scrollWidth;
    travel = length - width;
    if (travel < MIN_TRAVEL) return unpin();

    const origin = track.getBoundingClientRect().left;
    centers = items.map((item) => {
      const rect = item.getBoundingClientRect();
      return rect.left - origin + rect.width / 2;
    });

    pinned = true;
    viewport.scrollLeft = 0;
    root.style.setProperty('--pr-wall-travel', `${Math.round(travel)}px`);
    root.classList.add('is-pinned');
    buildTicks();
  };

  const frame = () => {
    ticking = false;
    if (!pinned) return;
    const p = clamp(-root.getBoundingClientRect().top / travel);
    const x = p * travel;
    track.style.transform = `translate3d(${-x.toFixed(1)}px, 0, 0)`;
    // The sprayed part of the line is the stretch of wall seen so far
    if (fill) fill.style.transform = `scaleX(${((x + width) / length).toFixed(4)})`;
    ticks.forEach((tick, i) => tick.classList.toggle('is-passed', centers[i] <= x + width));
  };

  const request = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  };

  const refresh = () => {
    measure();
    frame();
  };

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', refresh);
  wide.addEventListener('change', refresh);
  reduce.addEventListener('change', refresh);
  // Sleeve images and the display font change the wall's length
  window.addEventListener('load', refresh);
  document.fonts?.ready.then(refresh);
  refresh();

  // ---- Keyboard: bring a focused poster (or the end link) into the window ----
  track.addEventListener('focusin', (event) => {
    if (!pinned) return;
    const target = event.target.closest('[data-pr-wall-item], .pr-wall__end');
    if (!target) return;
    const rect = target.getBoundingClientRect();
    if (rect.left >= 0 && rect.right <= width) return;
    // Where the target sits along the wall (the track and the target move together)
    const center = rect.left - track.getBoundingClientRect().left + rect.width / 2;
    const wanted = Math.min(travel, Math.max(0, center - width / 2));
    const top = root.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + wanted, behavior: 'auto' });
  });

  // ---- Paste-in as posters come into view (they are never hidden) ----
  if (!reduce.matches && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-pasted');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );
    items.forEach((item) => observer.observe(item));
  }
})();
